// src/scenes/meal-entry/MealCountsDashboard.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    useTheme,
    Modal,
    IconButton,
    Switch,
    FormControlLabel,
    CircularProgress,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartTooltip,
    Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { tokens } from "../../theme";

const API_MEAL_COUNTS = "https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts";

export default function MealCountsDashboard() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    // raw data & UI state
    const [data, setData] = useState([]);
    const [siteFilter, setSiteFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [comparePrev, setComparePrev] = useState(false);
    const [loading, setLoading] = useState(true);

    // pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);

    // compute current date & default SY
    const today = new Date();
    const defaultSYStartYear = today.getMonth() >= 6
        ? today.getFullYear()
        : today.getFullYear() - 1;
    const [selectedSYStartYear, setSelectedSYStartYear] = useState(defaultSYStartYear);

    // fetch once
    useEffect(() => {
        setLoading(true);
        fetch(API_MEAL_COUNTS)
            .then(r => r.json())
            .then(rows => {
                setData(rows);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, []);

    // build dropdown options
    const schoolYears = useMemo(() => {
        const years = new Set();
        data.forEach(r => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            const sy = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
            years.add(sy);
        });
        return Array.from(years)
            .sort((a, b) => b - a)
            .map(y => ({ value: y, label: `${y}-${y + 1}` }));
    }, [data]);

    const sites = useMemo(
        () => Array.from(new Set(data.map(r => r.SchoolName).filter(Boolean))),
        [data]
    );

    const months = useMemo(() => {
        const setM = new Set();
        data.forEach(r => {
            const d = new Date(r.Date);
            if (!isNaN(d)) {
                setM.add(`${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`);
            }
        });
        return Array.from(setM).sort((a, b) => new Date(a) - new Date(b));
    }, [data]);

    // compute SY boundaries
    const syStart = new Date(selectedSYStartYear, 6, 1);
    const syEnd = new Date(selectedSYStartYear + 1, 5, 30);
    const prevStart = new Date(selectedSYStartYear - 1, 6, 1);
    const prevEnd = new Date(selectedSYStartYear, 5, 30);

    // filter rows to current SY + dropdown filters
    const filtered = useMemo(
        () => data.filter(r => {
            const d = new Date(r.Date);
            if (isNaN(d) || d < syStart || d > syEnd) return false;
            if (siteFilter && r.SchoolName !== siteFilter) return false;
            if (monthFilter) {
                return `${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}` === monthFilter;
            }
            return true;
        }),
        [data, syStart, syEnd, siteFilter, monthFilter]
    );

    // slice for pagination
    const paginatedRows = useMemo(
        () => filtered.slice(page * pageSize, page * pageSize + pageSize),
        [filtered, page, pageSize]
    );

    // aggregate for chart
    const { currentAgg, prevAgg } = useMemo(() => {
        const curr = {}, prev = {};
        data.forEach(r => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            const m = d.toLocaleString("en-US", { month: "short" });
            if (d >= syStart && d <= syEnd) {
                curr[m] = curr[m] || { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
                curr[m].Breakfast += +r.BR_Free + +r.BR_Paid + +r.BR_Reduced;
                curr[m].Lunch += +r.LN_Free + +r.LN_Paid + +r.LN_Reduced;
                curr[m].Snack += +r.SN_Free;
                curr[m].Supper += +r.SP_Free + +r.SP_Paid + +r.SP_Reduced;
            }
            if (d >= prevStart && d <= prevEnd) {
                prev[m] = prev[m] || { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
                prev[m].Breakfast += +r.BR_Free + +r.BR_Paid + +r.BR_Reduced;
                prev[m].Lunch += +r.LN_Free + +r.LN_Paid + +r.LN_Reduced;
                prev[m].Snack += +r.SN_Free;
                prev[m].Supper += +r.SP_Free + +r.SP_Paid + +r.SP_Reduced;
            }
        });
        return { currentAgg: curr, prevAgg: prev };
    }, [data, syStart, syEnd, prevStart, prevEnd]);

    // fixed month order July→June
    const monthOrder = useMemo(
        () => Array.from({ length: 12 }).map((_, i) =>
            new Date(0, (6 + i) % 12).toLocaleString("en-US", { month: "short" })
        ),
        []
    );

    // assemble chartData
    const chartData = useMemo(
        () => monthOrder.map(m => ({
            month: m,
            Breakfast: currentAgg[m]?.Breakfast || 0,
            Lunch: currentAgg[m]?.Lunch || 0,
            Snack: currentAgg[m]?.Snack || 0,
            Supper: currentAgg[m]?.Supper || 0,
            BreakfastPrev: prevAgg[m]?.Breakfast || 0,
            LunchPrev: prevAgg[m]?.Lunch || 0,
            SnackPrev: prevAgg[m]?.Snack || 0,
            SupperPrev: prevAgg[m]?.Supper || 0,
        })),
        [currentAgg, prevAgg, monthOrder]
    );

    // handle inline edits
    const processRowUpdate = useCallback(async (newRow, oldRow) => {
        try {
            await fetch(`${API_MEAL_COUNTS}/${newRow.apiid}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newRow),
            });
            setData(prev => prev.map(r => r.apiid === newRow.apiid ? newRow : r));
            return newRow;
        } catch {
            return oldRow;
        }
    }, []);

    // grid columns
    const columns = [
        {
            field: "Date",
            headerName: "Date",
            flex: 1,
            valueFormatter: ({ value }) => {
                const d = new Date(value);
                return isNaN(d) ? value : d.toLocaleDateString("en-US");
            },
        },
        { field: "SchoolName", headerName: "Site", flex: 1 },
        { field: "BR_Free", headerName: "BR Free", type: "number", flex: 1, editable: true },
        { field: "BR_Paid", headerName: "BR Paid", type: "number", flex: 1, editable: true },
        { field: "BR_Reduced", headerName: "BR Reduced", type: "number", flex: 1, editable: true },
        { field: "LN_Free", headerName: "LN Free", type: "number", flex: 1, editable: true },
        { field: "LN_Paid", headerName: "LN Paid", type: "number", flex: 1, editable: true },
        { field: "LN_Reduced", headerName: "LN Reduced", type: "number", flex: 1, editable: true },
        { field: "SN_Free", headerName: "SN Free", type: "number", flex: 1, editable: true },
        { field: "SP_Free", headerName: "SP Free", type: "number", flex: 1, editable: true },
        { field: "SP_Paid", headerName: "SP Paid", type: "number", flex: 1, editable: true },
        { field: "SP_Reduced", headerName: "SP Reduced", type: "number", flex: 1, editable: true },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: params => (
                <IconButton
                    color="error"
                    onClick={async () => {
                        await fetch(`${API_MEAL_COUNTS}/${params.row.apiid}`, { method: "DELETE" });
                        setData(prev => prev.filter(r => r.apiid !== params.row.apiid));
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            )
        }
    ];

    // CSV export
    const handleDownload = () => {
        if (!filtered.length) return;
        const headers = columns.filter(c => c.field !== "actions").map(c => c.headerName);
        const rowsCsv = filtered.map(row =>
            columns.filter(c => c.field !== "actions").map(col => {
                const v = row[col.field] ?? "";
                return typeof v === "string" ? `"${v}"` : v;
            }).join(",")
        );
        const csv = [headers.join(","), ...rowsCsv].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "meal_counts_filtered.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {/* Loading */}
            <Modal open={loading} disableEscapeKeyDown>
                <Box sx={{
                    position: "absolute", top: "50%", left: "50%",
                    transform: "translate(-50%,-50%)",
                    bgcolor: theme.palette.background.paper,
                    p: 4, borderRadius: 2, textAlign: "center"
                }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading meal counts…</Typography>
                </Box>
            </Modal>

            <Box m="20px">
                <Header title="Meal Counts Dashboard" subtitle="Jul 1 – Jun 30" />

                {/* Controls */}
                <Box display="flex" gap="20px" mb="20px" alignItems="center">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>School Year</InputLabel>
                        <Select
                            value={selectedSYStartYear}
                            onChange={e => setSelectedSYStartYear(e.target.value)}
                            label="School Year"
                        >
                            {schoolYears.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Site</InputLabel>
                        <Select
                            value={siteFilter}
                            onChange={e => setSiteFilter(e.target.value)}
                            label="Site"
                        >
                            <MenuItem value="">All Sites</MenuItem>
                            {sites.map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Month</InputLabel>
                        <Select
                            value={monthFilter}
                            onChange={e => setMonthFilter(e.target.value)}
                            label="Month"
                        >
                            <MenuItem value="">All Months</MenuItem>
                            {months.map(m => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={comparePrev}
                                onChange={e => setComparePrev(e.target.checked)}
                            />
                        }
                        label="Compare Prev Year"
                    />

                    <Button
                        variant="contained"
                        sx={{ ml: "auto", backgroundColor: "#00AAFF", fontWeight: "bold" }}
                        onClick={handleDownload}
                    >
                        Download CSV
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: "#FF4500", fontWeight: "bold" }}
                        onClick={() => navigate("/mealcountsentry")}
                    >
                        Enter Meal Count
                    </Button>
                </Box>

                {/* Chart */}
                <Box height={300} mb="20px" sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}>
                    <ResponsiveContainer>
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="gradBreakfast" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#FF4136" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="#FF4136" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gradLunch" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0074D9" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="#0074D9" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gradSnack" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2ECC40" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="#2ECC40" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gradSupper" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#FFDC00" stopOpacity={0.6} />
                                    <stop offset="95%" stopColor="#FFDC00" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#555" strokeDasharray="3 3" />
                            <XAxis dataKey="month" stroke="#EEE" />
                            <YAxis stroke="#EEE" />
                            <RechartTooltip contentStyle={{ backgroundColor: "#222" }} />
                            <Legend wrapperStyle={{ color: "#EEE" }} />

                            {comparePrev && (
                                <>
                                    <Area
                                        type="monotone"
                                        dataKey="BreakfastPrev"
                                        stroke="#FF4136"
                                        strokeWidth={3}
                                        dot={false}
                                        strokeDasharray="5 5"
                                        name="Breakfast (Prev)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="LunchPrev"
                                        stroke="#0074D9"
                                        strokeWidth={3}
                                        dot={false}
                                        strokeDasharray="5 5"
                                        name="Lunch (Prev)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="SnackPrev"
                                        stroke="#2ECC40"
                                        strokeWidth={3}
                                        dot={false}
                                        strokeDasharray="5 5"
                                        name="Snack (Prev)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="SupperPrev"
                                        stroke="#FFDC00"
                                        strokeWidth={3}
                                        dot={false}
                                        strokeDasharray="5 5"
                                        name="Supper (Prev)"
                                    />
                                </>
                            )}

                            <Area
                                type="monotone"
                                dataKey="Breakfast"
                                stroke="#FF4136"
                                fill="url(#gradBreakfast)"
                                name="Breakfast"
                            />
                            <Area
                                type="monotone"
                                dataKey="Lunch"
                                stroke="#0074D9"
                                fill="url(#gradLunch)"
                                name="Lunch"
                            />
                            <Area
                                type="monotone"
                                dataKey="Snack"
                                stroke="#2ECC40"
                                fill="url(#gradSnack)"
                                name="Snack"
                            />
                            <Area
                                type="monotone"
                                dataKey="Supper"
                                stroke="#FFDC00"
                                fill="url(#gradSupper)"
                                name="Supper"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>

                {/* Paginated Table */}
                <Box height={600} sx={{
                    "& .MuiDataGrid-root": { border: "none", color: theme.palette.text.primary },
                    "& .MuiDataGrid-cell": { color: theme.palette.text.primary },
                    "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.primary[500], color: theme.palette.text.primary },
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
                    "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: theme.palette.action.hover },
                    "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: theme.palette.background.paper },
                }}>
                    <DataGrid
                        rows={paginatedRows.map((r, i) => ({ id: i, ...r }))}
                        columns={columns}
                        pagination
                        page={page}
                        pageSize={pageSize}
                        rowsPerPageOptions={[10, 20, 50]}
                        rowCount={filtered.length}
                        paginationMode="client"
                        onPageChange={newPage => setPage(newPage)}
                        onPageSizeChange={newSize => { setPageSize(newSize); setPage(0); }}
                        disableSelectionOnClick
                        editMode="cell"
                        experimentalFeatures={{ newEditingApi: true }}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={err => console.error(err)}
                    />
                </Box>


            </Box>
        </>
    );
}
